/**
 * Knowledge library / RAG domain types.
 *
 * Shared by `services/knowledge`, `services/vector`, `stores/knowledgeStore`
 * and `KnowledgePage`. See `KNOWLEDGE_SYSTEM.md` for the pipeline this feeds.
 */

export type DocumentStatus = "queued" | "processing" | "ready" | "error";

export interface Collection {
  readonly id: string;
  name: string;
  readonly createdAt: number;
  /** `null` only for pre-Phase-4 rows not yet backfilled. */
  workspaceId: string | null;
}

export interface KnowledgeDocument {
  readonly id: string;
  collectionId: string | null;
  name: string;
  /** Absolute path of the original file, kept for re-processing/incremental
   * updates — the extracted text/chunks are what's actually indexed. */
  sourcePath: string;
  fileType: string;
  sizeBytes: number;
  readonly importedAt: number;
  status: DocumentStatus;
  error: string | null;
  tags: string[];
  chunkCount: number;
}

export interface KnowledgeChunk {
  readonly id: string;
  readonly documentId: string;
  readonly seq: number;
  readonly content: string;
}

export interface KnowledgeSearchResult {
  readonly chunk: KnowledgeChunk;
  readonly documentId: string;
  readonly documentName: string;
  readonly score: number;
}

/** Which slice of the knowledge base a conversation should draw on. */
export type KnowledgeScopeMode = "all" | "collections" | "folder" | "none";

export interface KnowledgeScope {
  mode: KnowledgeScopeMode;
  collectionIds?: string[];
  folderPath?: string;
}

export const DEFAULT_KNOWLEDGE_SCOPE: KnowledgeScope = { mode: "none" };
